import { signal } from '@preact/signals-react';
import moment from 'utils/moment';
import { ICustomerUI } from 'types/customer';
import { IDatePickerValue } from './TimeTab/DatePicker';
import { ADD_APPOINTMENT_TABS } from './helper';
import { APPOINTMENT_GROUP_VALUE } from './ReviewTab/RadioGroup';
import { AppointmentStatus } from 'Turn/services/constants';
import { IServiceItemData } from 'types/appointment';
import { dateParamSignal } from 'CalendarFilter/DatePicker';
import React from 'react';
import { AddCustomerRef } from './CustomerTab/AddCustomer';
import { getGroupNoteMarker, getGroupNoteResidual, joinGroupNote } from 'Calendar/appointment-style-a';

const openSignal = signal<boolean>(false);

const tabSignal = signal<string>(ADD_APPOINTMENT_TABS.CUSTOMER);

const timeSignal = signal<IDatePickerValue>(null);

const activeDateSignal = signal<IDatePickerValue>(moment());

const staffIdSignal = signal<string | null>(null);

const staffIdMasterSignal = signal<string | null>(null);

const customerSignal = signal<ICustomerUI | null>(null);

const defaultCustomerSignal = signal<ICustomerUI | null>(null);

const servicesSignal = signal<{ serviceId: string, staffId: string, name: string, duration: number, price: number }[]>([]);

const detailIdSignal = signal<string | null>(null);

/** Service id cua lich luc mo modal chi tiet. Dung de biet nguoi dung co doi dich vu hay khong. */
const detailServiceIdsSignal = signal<string[]>([]);

const detailStatusSignal = signal<AppointmentStatus | null>(null);
/** seriesId cua lich dang mo. Co gia tri = lich thuoc mot chuoi repeat. */
const detailSeriesIdSignal = signal<string | null>(null);

const noteSignal = signal<string | null>(null);

/**
 * Marker `Group:` cua lich dang mo, tach khoi o ghi chu.
 *
 * Marker nam chung mot truong `note` voi ghi chu vi ban ghi lich hen khong co truong nao
 * khac danh dau lich nhom. Giu rieng o day de nguoi dung chi thay va sua ghi chu that,
 * con marker duoc ghep lai nguyen ven luc gui di. null = lich khong thuoc nhom.
 */
const detailGroupMarkerSignal = signal<string | null>(null);

const groupTabSignal = signal<APPOINTMENT_GROUP_VALUE>(APPOINTMENT_GROUP_VALUE.INDIVIDUAL_APT);

const groupValueSignal = signal<string | null>(null);

const isViewOnly = signal<boolean>(false);

const isQuickBooking = signal<boolean>(false);

const requestStaff = signal<boolean>(false);

export const totalDurationSignal = signal<number>(0);
export const totalDurationSignalDefault = signal<number>(0);
export const addCustomerRef = React.createRef<AddCustomerRef>();


const moveToTab = (tab: 'customer' | 'staff' | 'time' | 'service' | 'review') => {
  switch (tab) {
    case 'customer':
      tabSignal.value = ADD_APPOINTMENT_TABS.CUSTOMER;
      break;
    case 'staff':
      tabSignal.value = ADD_APPOINTMENT_TABS.STAFF;
      break;
    case 'time':
      tabSignal.value = ADD_APPOINTMENT_TABS.TIME;
      break;
    case 'service':
      tabSignal.value = ADD_APPOINTMENT_TABS.SERVICE;
      break;
    case 'review':
      tabSignal.value = ADD_APPOINTMENT_TABS.REVIEW;
      break;

    default:
      break;
  }
};

const setActiveKey = (val: string) => {
  aptSignal.tabSignal.value = val;
};

const navToReview = () => {
  setActiveKey(ADD_APPOINTMENT_TABS.REVIEW);
};

const next = () => {
  const { tabSignal, customerSignal, staffIdSignal, timeSignal, servicesSignal } = aptSignal;
  switch (tabSignal.value) {
    case ADD_APPOINTMENT_TABS.CUSTOMER: {
      if (staffIdSignal.value && timeSignal.value && servicesSignal.value.length)
        return !!customerSignal.value?.id && setActiveKey(ADD_APPOINTMENT_TABS.REVIEW);

      if (staffIdSignal.value && timeSignal.value)
        return !!customerSignal.value?.id && setActiveKey(ADD_APPOINTMENT_TABS.SERVICE);

      if (staffIdSignal.value)
        return !!customerSignal.value?.id && setActiveKey(ADD_APPOINTMENT_TABS.TIME);

      return !!customerSignal.value?.id && setActiveKey(ADD_APPOINTMENT_TABS.STAFF);
    }
    case ADD_APPOINTMENT_TABS.STAFF: {
      if (timeSignal.value && servicesSignal.value.length) {
        return !!staffIdSignal.value && setActiveKey(ADD_APPOINTMENT_TABS.REVIEW);
      }

      return !!staffIdSignal.value && setActiveKey(ADD_APPOINTMENT_TABS.TIME);
    }
    case ADD_APPOINTMENT_TABS.TIME: {
      if (servicesSignal.value.length) {
        return !!timeSignal.value && setActiveKey(ADD_APPOINTMENT_TABS.REVIEW);
      }

      return !!timeSignal.value && setActiveKey(ADD_APPOINTMENT_TABS.SERVICE);
    }
    case ADD_APPOINTMENT_TABS.SERVICE:
      return !!servicesSignal.value?.length && setActiveKey(ADD_APPOINTMENT_TABS.REVIEW);
    default:
      break;
  }
};

const getDisabled = () => {
  const { tabSignal, customerSignal, staffIdSignal, timeSignal, servicesSignal } = aptSignal;
  switch (tabSignal.value) {
    case ADD_APPOINTMENT_TABS.CUSTOMER:
      return !customerSignal.value?.id;
    case ADD_APPOINTMENT_TABS.STAFF:
      return !staffIdSignal.value;
    case ADD_APPOINTMENT_TABS.TIME:
      return !timeSignal.value;
    case ADD_APPOINTMENT_TABS.SERVICE:
      return !servicesSignal.value?.length;
    case ADD_APPOINTMENT_TABS.REVIEW:
      return !aptSignal.getValid();
    default:
      break;
  }
};

const getValid = () => {
  const { customerSignal, staffIdSignal, timeSignal, servicesSignal } = aptSignal;
  return !!customerSignal.value?.id && !!staffIdSignal.value && !!timeSignal.value && !!servicesSignal.value?.length;
};

/**
 * Lich da CONFIRMED hoac CHECK_IN thi khong cho doi tho nua: khach da duoc bao ten tho,
 * hoac tho da nhan khach. Moi loi doi tho trong modal chi tiet deu doc cung mot co nay.
 */
const isStaffLocked = () => {
  const status = detailStatusSignal.value;
  return status === AppointmentStatus.CONFIRMED || status === AppointmentStatus.CHECK_IN;
};

/** What the form holds right now; the shape `getValues` returns and `reopenAtTime` takes back. */
export type IAptFormValues = {
  time: IDatePickerValue;
  staffId: string | null;
  customer: ICustomerUI | null;
  services: { serviceId: string, staffId: string, name: string, duration: number, price: number }[];
  totalDuration: number;
  note: string;
  totalPeople: number;
  requestStaff: boolean;
  groupTab: APPOINTMENT_GROUP_VALUE;
  staffIdMaster: string | null;
};

const getValues = (): IAptFormValues => {
  const time = timeSignal.value;
  const newDateValue = aptSignal.activeDateSignal.value?.clone().set({
    hour: time?.get('hour'),
    minute: time?.get('minute'),
    second: time?.get('second'),
  });

  return ({
    time: newDateValue,
    staffId: staffIdSignal.value,
    customer: customerSignal.value,
    services: servicesSignal.value,
    totalDuration: totalDurationSignal.value || totalDurationSignalDefault.value,
    note: joinGroupNote(detailGroupMarkerSignal.value, noteSignal.value),
    totalPeople: +(groupValueSignal.value || '') || 1,
    requestStaff: requestStaff.value,
    groupTab: groupTabSignal.value,
    staffIdMaster: staffIdMasterSignal.value,
  });
};

const aptSignal = {
  open: (date?: string, staffId?: string) => {
    openSignal.value = true;
    detailGroupMarkerSignal.value = null;
    timeSignal.value = date ? moment(date) : null;
    activeDateSignal.value = date ? moment(date) : (dateParamSignal.value || moment());
    staffIdSignal.value = staffId || null;
    isViewOnly.value = false;
    detailServiceIdsSignal.value = [];
    requestStaff.value = staffId ? true : false;
    isQuickBooking.value = false;
    defaultCustomerSignal.value = null;
    staffIdMasterSignal.value = null;
  },
  openWithCustomer: (customer: ICustomerUI) => {
    openSignal.value = true;
    detailGroupMarkerSignal.value = null;
    timeSignal.value = null;
    activeDateSignal.value = (dateParamSignal.value || moment());
    staffIdSignal.value = null;
    staffIdMasterSignal.value = null;
    isViewOnly.value = false;
    detailServiceIdsSignal.value = [];
    requestStaff.value = false;
    isQuickBooking.value = false;
    if (!customer.id) {
      setTimeout(() => {
        addCustomerRef.current?.open({ phoneNumber: customer.phone });
      }, 100);
    } else {
      customerSignal.value = customer;
      defaultCustomerSignal.value = customer;
      tabSignal.value = ADD_APPOINTMENT_TABS.STAFF;
    }
  },
  openDetail: (data: {
    id: string;
    customer: ICustomerUI;
    staffId: string;
    time: string;
    totalDuration: number;
    services: IServiceItemData[];
    quickBook?: boolean;
    note?: string;
    isViewOnly?: boolean;
    requestStaff?: boolean;
    status?: AppointmentStatus;
    seriesId?: string | null;
  }) => {
    openSignal.value = true;
    detailIdSignal.value = data.id;
    detailStatusSignal.value = data.status || null;
    detailSeriesIdSignal.value = data.seriesId || null;
    timeSignal.value = data.time ? moment(data.time) : null;
    activeDateSignal.value = data.time ? moment(data.time) : moment();
    staffIdSignal.value = data.staffId || null;
    staffIdMasterSignal.value = null;
    servicesSignal.value = data.services.map(o => ({
      serviceId: o.serviceId,
      staffId: data.staffId || '',
      name: o.serviceName,
      duration: o.duration || 0,
      price: o.price || 0,
    }));
    detailServiceIdsSignal.value = data.services.map(o => o.serviceId);
    customerSignal.value = data.customer;
    tabSignal.value = ADD_APPOINTMENT_TABS.REVIEW;
    detailGroupMarkerSignal.value = getGroupNoteMarker(data.note);
    noteSignal.value = detailGroupMarkerSignal.value
      ? (getGroupNoteResidual(data.note) || '')
      : (data.note || '');
    groupTabSignal.value = APPOINTMENT_GROUP_VALUE.INDIVIDUAL_APT;
    isViewOnly.value = data.isViewOnly || false;
    isQuickBooking.value = data.quickBook || false;
    requestStaff.value = data.requestStaff || false;
    totalDurationSignalDefault.value = data.totalDuration || 0;
    defaultCustomerSignal.value = null;
  },
  /**
   * Puts the form back the way it was before BOOK closed it, on the Time tab: used when the
   * server rejects an optimistic booking as a conflict, so the staff only has to pick another slot.
   */
  reopenAtTime: (values: IAptFormValues): boolean => {
    // The user may have opened another form (or an appointment's detail) while the booking was in
    // flight; overwriting it could save these values onto the wrong appointment. Leave it alone.
    if (openSignal.value) return false;
    // close() clears every detail/master signal so the form comes back in plain "new booking" mode.
    aptSignal.close();
    openSignal.value = true;
    customerSignal.value = values.customer;
    defaultCustomerSignal.value = values.customer;
    staffIdSignal.value = values.staffId;
    activeDateSignal.value = values.time ? values.time.clone() : (dateParamSignal.value || moment());
    timeSignal.value = values.time ? values.time.clone() : null;
    servicesSignal.value = values.services;
    noteSignal.value = values.note || null;
    groupValueSignal.value = values.totalPeople > 1 ? String(values.totalPeople) : null;
    // close() resets these too; without them a group booking comes back as Individual and the
    // master staff is dropped, so the retry would be built with the wrong staff per service.
    groupTabSignal.value = values.groupTab;
    staffIdMasterSignal.value = values.staffIdMaster;
    requestStaff.value = values.requestStaff;
    tabSignal.value = ADD_APPOINTMENT_TABS.TIME;
    return true;
  },
  close: () => {
    openSignal.value = false;
    timeSignal.value = null;
    staffIdSignal.value = null;
    tabSignal.value = ADD_APPOINTMENT_TABS.CUSTOMER;
    customerSignal.value = null;
    servicesSignal.value = [];
    detailIdSignal.value = null;
    detailServiceIdsSignal.value = [];
    detailStatusSignal.value = null;
    detailSeriesIdSignal.value = null;
    totalDurationSignal.value = 0;
    noteSignal.value = null;
    detailGroupMarkerSignal.value = null;
    groupValueSignal.value = null;
    groupTabSignal.value = APPOINTMENT_GROUP_VALUE.INDIVIDUAL_APT;
    isViewOnly.value = false;
    requestStaff.value = false;
    staffIdMasterSignal.value = null;
  },
  next,
  moveToTab,
  openSignal,
  timeSignal,
  staffIdSignal,
  tabSignal,
  customerSignal,
  servicesSignal,
  getValid,
  getDisabled,
  setActiveKey,
  getValues,
  detailIdSignal,
  detailServiceIdsSignal,
  detailSeriesIdSignal,
  detailStatusSignal,
  isStaffLocked,
  noteSignal,
  detailGroupMarkerSignal,
  groupTabSignal,
  activeDateSignal,
  groupValueSignal,
  isViewOnly,
  isQuickBooking,
  requestStaff,
  navToReview,
  totalDurationSignal,
  totalDurationSignalDefault,
  defaultCustomerSignal,
  staffIdMasterSignal,
};

export default aptSignal;
